import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Grid, Paper, Typography, Card, CardContent, Avatar, FormControl, InputLabel,
  Select, MenuItem, Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Divider, Stack, useTheme, useMediaQuery, Autocomplete, TextField,
  TablePagination,
} from "@mui/material";
import { jwtDecode } from "jwt-decode";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import DownloadIcon from "@mui/icons-material/Download";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, LabelList,
} from "recharts";
import {
  getDashboardImpactoAdmin, getDashboardImpactoUsuario,
  exportarDashboardAdminExcel, exportarDashboardUsuarioExcel,
} from "../../componentes/http/actions/DashboardImpactoAction";
import { getOrganismosClientes, getOrganismoEmisorHabilitado } from "../../componentes/http/actions/OrganismoEmisorAction";
import { getTipoDocumentoHabilitado } from "../../componentes/http/actions/TipoDocumentoAction";
import DetalleImpactoModal from "../../componentes/modal/Normativa/DetalleImpactoModal";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const COLOR_VERDE = "#43A047";
const COLOR_AMARILLO = "#FB8C00";
const COLOR_ROJO = "#E53935";
const COLOR_AZUL = "#1565C0";
const COLOR_TEAL = "#00897B";

const TOP_N = 8;
const CHART_HEIGHT = 260;

const PERIODOS = [
  { value: "semana_actual", label: "Esta semana" },
  { value: "mes_actual", label: "Este mes" },
  { value: "3", label: "Últimos 3 meses" },
  { value: "6", label: "Últimos 6 meses" },
  { value: "12", label: "Últimos 12 meses" },
  { value: "0", label: "Todo el histórico" },
  { value: "personalizado", label: "Rango personalizado…" },
];

const formatFecha = (value) =>
  value ? new Date(value).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";

const colorHex = (color) => {
  switch ((color || "").toLowerCase()) {
    case "rojo": return COLOR_ROJO;
    case "amarillo": return COLOR_AMARILLO;
    case "verde": return COLOR_VERDE;
    default: return "#9E9E9E";
  }
};

const nivelImpacto = (color) => {
  switch ((color || "").toLowerCase()) {
    case "rojo": return "Impacto alto";
    case "amarillo": return "Impacto medio";
    case "verde": return "Impacto bajo";
    default: return "Sin evaluar";
  }
};

const agruparTopN = (items, n = TOP_N) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const ordenados = [...items].sort((a, b) => (b.valor || 0) - (a.valor || 0));
  if (ordenados.length <= n) return ordenados;
  const top = ordenados.slice(0, n);
  const resto = ordenados.slice(n);
  const sumaResto = resto.reduce((acc, x) => acc + (x.valor || 0), 0);
  if (sumaResto > 0) {
    top.push({ etiqueta: `Otros (${resto.length})`, valor: sumaResto });
  }
  return top;
};

const rangoDesdePeriodo = (periodo) => {
  const hoy = new Date();

  const inicioDia = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  const finDia = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

  switch (periodo) {
    case "semana_actual": {
      const d = new Date(hoy);
      const dow = (d.getDay() + 6) % 7;
      d.setDate(d.getDate() - dow);
      return { desde: inicioDia(d), hasta: finDia(hoy) };
    }
    case "semana_pasada": {
      const lunesEsta = new Date(hoy);
      const dow = (lunesEsta.getDay() + 6) % 7;
      lunesEsta.setDate(lunesEsta.getDate() - dow);
      const lunesPasada = new Date(lunesEsta);
      lunesPasada.setDate(lunesPasada.getDate() - 7);
      const domingoPasada = new Date(lunesEsta);
      domingoPasada.setDate(domingoPasada.getDate() - 1);
      return { desde: inicioDia(lunesPasada), hasta: finDia(domingoPasada) };
    }
    case "mes_actual": {
      const primero = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      return { desde: inicioDia(primero), hasta: finDia(hoy) };
    }
    case "mes_pasado": {
      const primeroPasado = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
      const ultimoPasado = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
      return { desde: inicioDia(primeroPasado), hasta: finDia(ultimoPasado) };
    }
    case "0":
      return { desde: null, hasta: null };
    case "3":
    case "6":
    case "12": {
      const d = new Date(hoy);
      d.setMonth(d.getMonth() - parseInt(periodo, 10));
      return { desde: inicioDia(d), hasta: finDia(hoy) };
    }
    default:
      return { desde: null, hasta: null };
  }
};

const KpiCard = ({ icon, label, value, color }) => (
  <Card elevation={1} sx={{ height: "100%" }}>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
      <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>{icon}</Avatar>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color }}>{value}</Typography>
      </Box>
    </CardContent>
  </Card>
);

const SectionCard = ({ title, action, children, minHeight = 340 }) => (
  <Paper elevation={1} sx={{ p: 2.5, height: "100%", minHeight, display: "flex", flexDirection: "column" }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
      {action}
    </Box>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Paper>
);

const TickTruncado = (maxLen) => ({ x, y, payload }) => {
  const txt = payload.value.length > maxLen ? payload.value.slice(0, maxLen - 1) + "…" : payload.value;
  return (
    <text x={x} y={y} dy={4} textAnchor="end" fontSize={11} fill="#555">
      <title>{payload.value}</title>
      {txt}
    </text>
  );
};

const DashboardImpacto = () => {
  const token = localStorage.getItem("token");
  const decoded = jwtDecode(token);
  const idUsuario = decoded["oid"];
  const esAdmin = decoded.rol === "Administrador";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  const [organismos, setOrganismos] = useState([]);
  const [organismoSel, setOrganismoSel] = useState(null);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [tipoDocumentoSel, setTipoDocumentoSel] = useState(null);
  const [periodo, setPeriodo] = useState("12");
  const [rangoDesde, setRangoDesde] = useState(null);
  const [rangoHasta, setRangoHasta] = useState(null);
  const [colorImpacto, setColorImpacto] = useState("todos");

  const [pageNormas, setPageNormas] = useState(0);
  const [rowsPerPageNormas, setRowsPerPageNormas] = useState(10);

  const [openImpactoModal, setOpenImpactoModal] = useState(false);
  const [normativaImpacto, setNormativaImpacto] = useState(null);

  const handleOpenImpacto = (n) => {
    setNormativaImpacto({ id: n.id, nombre: n.nombre });
    setOpenImpactoModal(true);
  };

  useEffect(() => {
    const cargarOrganismos = async () => {
      try {
        let res;
        if (esAdmin) {
          res = await getOrganismoEmisorHabilitado(token);
        } else {
          try {
            res = await getOrganismosClientes(token, idUsuario);
          } catch (error) {
            console.warn("Error obteniendo organismos del cliente, usando habilitados como respaldo:", error);
            res = await getOrganismoEmisorHabilitado(token);
          }
        }
        const items = Array.isArray(res) ? res : (res?.items || []);
        if (!esAdmin && items.length === 0) {
          const resFallback = await getOrganismoEmisorHabilitado(token);
          const itemsFallback = Array.isArray(resFallback) ? resFallback : (resFallback?.items || []);
          setOrganismos(itemsFallback);
        } else {
          setOrganismos(items);
        }
      } catch (error) {
        console.error("Error al cargar organismos:", error);
        setOrganismos([]);
      }
    };
    cargarOrganismos();
  }, [token, idUsuario, esAdmin]);

  useEffect(() => {
    getTipoDocumentoHabilitado(token)
      .then((res) => {
        const items = Array.isArray(res) ? res : (res?.items || []);
        setTiposDocumento(items);
      })
      .catch(() => setTiposDocumento([]));
  }, [token]);

  const filtros = useMemo(() => {
    let desde, hasta;
    if (periodo === "personalizado") {
      desde = rangoDesde;
      hasta = rangoHasta;
    } else {
      const r = rangoDesdePeriodo(periodo);
      desde = r.desde;
      hasta = r.hasta;
    }
    return {
      idOrganismo: organismoSel?.id || null,
      idTipoDocumento: tipoDocumentoSel?.id || null,
      fechaDesde: desde,
      fechaHasta: hasta,
      colorImpacto: colorImpacto !== "todos" ? colorImpacto : null,
    };
  }, [organismoSel, tipoDocumentoSel, periodo, rangoDesde, rangoHasta, colorImpacto]);

  const fetchDashboard = useCallback(() => {
    setLoading(true);
    const req = esAdmin
      ? getDashboardImpactoAdmin(token, filtros)
      : getDashboardImpactoUsuario(token, idUsuario, filtros);
    req
      .then((res) => setData(res?.item || null))
      .catch((error) => { console.error("Error al cargar dashboard:", error); setData(null); })
      .finally(() => setLoading(false));
  }, [token, idUsuario, esAdmin, filtros]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  useEffect(() => { setPageNormas(0); }, [data]);

  const handleExportar = () => {
    setIsDownloading(true);
    const req = esAdmin
      ? exportarDashboardAdminExcel(token, filtros)
      : exportarDashboardUsuarioExcel(token, idUsuario, filtros);
    req
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `DashboardImpacto_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch((error) => console.error("Error al exportar:", error))
      .finally(() => setIsDownloading(false));
  };

  const distribucionData = useMemo(() => {
    if (!data?.distribucion) return [];
    const d = data.distribucion;
    return [
      { name: "Impacto bajo", value: d.verde, color: COLOR_VERDE },
      { name: "Impacto medio", value: d.amarillo, color: COLOR_AMARILLO },
      { name: "Impacto alto", value: d.rojo, color: COLOR_ROJO },
    ].filter((x) => x.value > 0);
  }, [data]);

  const tipoDocumentoData = useMemo(
    () => agruparTopN(data?.porTipoDocumento),
    [data]
  );

  const normas = useMemo(
    () => (Array.isArray(data?.normasRecientes) ? data.normasRecientes : []),
    [data]
  );

  const normasPagina = useMemo(
    () => normas.slice(pageNormas * rowsPerPageNormas, pageNormas * rowsPerPageNormas + rowsPerPageNormas),
    [normas, pageNormas, rowsPerPageNormas]
  );

  const handleChangePageNormas = (e, nuevaPagina) => setPageNormas(nuevaPagina);
  const handleChangeRowsPerPageNormas = (e) => {
    setRowsPerPageNormas(parseInt(e.target.value, 10));
    setPageNormas(0);
  };

  const totalDist = data?.distribucion?.total || 0;
  const pct = (v) => (totalDist > 0 ? ((v / totalDist) * 100).toFixed(1) : "0.0");

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ pt: 4 }}>
        <Paper sx={{ p: 5, textAlign: "center" }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Sin datos</Typography>
          <Typography variant="body2" color="text.secondary">
            No se pudo cargar la información del dashboard. Intenta nuevamente o ajusta los filtros.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const k = data.kpis || {};

  return (
    <Box sx={{ pt: { xs: 2.5, sm: 1.5 }, pb: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Dashboard de Impacto Normativo</Typography>
          <Typography variant="body2" color="text.secondary">
            Criterios de impacto{data.incluyeEstadoGestion ? " y estado de gestión" : ""}
          </Typography>
        </Box>
        <Button
          variant="contained" color="success" startIcon={<DownloadIcon />}
          onClick={handleExportar} disabled={isDownloading}
        >
          {isDownloading ? <CircularProgress size={20} color="inherit" /> : "Exportar Excel"}
        </Button>
      </Box>

      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              size="small"
              options={organismos}
              getOptionLabel={(o) => o.nombre || ""}
              value={organismoSel}
              onChange={(e, v) => setOrganismoSel(v)}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              renderInput={(p) => <TextField {...p} label="Regulador / Organismo" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              size="small"
              options={tiposDocumento}
              getOptionLabel={(o) => o.nombre || ""}
              value={tipoDocumentoSel}
              onChange={(e, v) => setTipoDocumentoSel(v)}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              renderInput={(p) => <TextField {...p} label="Tipo de documento" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Período</InputLabel>
              <Select value={periodo} label="Período" onChange={(e) => setPeriodo(e.target.value)}>
                {PERIODOS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Nivel de impacto</InputLabel>
              <Select value={colorImpacto} label="Nivel de impacto" onChange={(e) => setColorImpacto(e.target.value)}>
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="Verde">Impacto bajo</MenuItem>
                <MenuItem value="Amarillo">Impacto medio</MenuItem>
                <MenuItem value="Rojo">Impacto alto</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {periodo === "personalizado" && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    sx={{ width: "100%" }} format="dd/MM/yyyy" label="Desde"
                    value={rangoDesde} onChange={setRangoDesde}
                    maxDate={rangoHasta || undefined}
                    slotProps={{ textField: { size: "small" } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    sx={{ width: "100%" }} format="dd/MM/yyyy" label="Hasta"
                    value={rangoHasta} onChange={setRangoHasta}
                    minDate={rangoDesde || undefined}
                    slotProps={{ textField: { size: "small" } }}
                  />
                </LocalizationProvider>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KpiCard icon={<DescriptionOutlinedIcon />} label="Total normas analizadas" value={k.totalAnalizadas ?? 0} color={COLOR_AZUL} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KpiCard icon={<ErrorOutlineIcon />} label="Impacto alto" value={k.impactoRojo ?? 0} color={COLOR_ROJO} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KpiCard icon={<WarningAmberIcon />} label="Impacto medio" value={k.impactoAmarillo ?? 0} color={COLOR_AMARILLO} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KpiCard icon={<CheckCircleOutlineIcon />} label="Impacto bajo" value={k.impactoVerde ?? 0} color={COLOR_VERDE} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KpiCard icon={<AccessTimeIcon />} label="Vigencia inmediata" value={k.vigenciaInmediata ?? 0} color={COLOR_TEAL} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KpiCard icon={<TaskAltIcon />} label="Sin evaluar" value={k.sinEvaluar ?? 0} color={COLOR_AZUL} />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={data.incluyeEstadoGestion ? 4 : 6}>
          <SectionCard title="Distribución por nivel de impacto">
            {distribucionData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Sin datos para mostrar.</Typography>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: "center",
                  gap: 2,
                  height: "100%",
                }}
              >
                <Stack spacing={1} sx={{ flex: 1, width: "100%", minWidth: 0 }}>
                  {distribucionData.map((d) => (
                    <Box key={d.name} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: d.color, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ flex: 1 }}>{d.name}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        {d.value} ({pct(d.value)}%)
                      </Typography>
                    </Box>
                  ))}
                </Stack>
                <Box sx={{ flex: 1, width: "100%", minWidth: 0 }}>
                  <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                    <PieChart>
                      <Pie
                        data={distribucionData} dataKey="value" nameKey="name"
                        cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2}
                      >
                        {distribucionData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <RTooltip formatter={(v, n) => [`${v} (${pct(v)}%)`, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            )}
          </SectionCard>
        </Grid>

        {data.incluyeEstadoGestion && (
          <Grid item xs={12} md={4}>
            <SectionCard title="Estado de gestión">
              {(!data.estadoGestion || data.estadoGestion.length === 0) ? (
                <Typography variant="body2" color="text.secondary">
                  Aún no hay normas con estado de seguimiento.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <BarChart data={data.estadoGestion} layout="vertical" margin={{ left: 40, right: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis
                      type="category"
                      dataKey="nombre"
                      width={170}
                      interval={0}
                      tick={TickTruncado(24)}
                    />
                    <RTooltip />
                    <Bar dataKey="cantidad" radius={[0, 4, 4, 0]}>
                      {data.estadoGestion.map((e, i) => (
                        <Cell key={i} fill={e.color || COLOR_AZUL} />
                      ))}
                      <LabelList dataKey="cantidad" position="right" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </SectionCard>
          </Grid>
        )}

        <Grid item xs={12} md={4}>
          <SectionCard title="Normas por tipo de documento">
            {tipoDocumentoData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Sin datos para mostrar.</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <BarChart data={tipoDocumentoData} layout="vertical" margin={{ left: 40, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="etiqueta"
                    width={140}
                    interval={0}
                    tick={TickTruncado(20)}
                  />
                  <RTooltip />
                  <Bar dataKey="valor" fill={COLOR_AZUL} radius={[0, 4, 4, 0]}>
                    <LabelList dataKey="valor" position="right" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </SectionCard>
        </Grid>
      </Grid>

       <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={5}>
          <SectionCard title="Normas por regulador">
            {(!data.alertasPorRegulador || data.alertasPorRegulador.length === 0) ? (
              <Typography variant="body2" color="text.secondary">Sin datos para mostrar.</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(260, data.alertasPorRegulador.length * 34)}>
                <BarChart data={data.alertasPorRegulador} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="etiqueta" width={90} tick={{ fontSize: 12 }} />
                  <RTooltip />
                  <Bar dataKey="valor" fill={COLOR_TEAL} radius={[0, 4, 4, 0]}>
                    <LabelList dataKey="valor" position="right" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={7}>
          <SectionCard title="Normas">
            {normas.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No hay normas para mostrar.</Typography>
            ) : isMobile ? (
              <>
                <Stack spacing={1.5}>
                  {normasPagina.map((n) => (
                    <Paper key={n.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{n.nombre}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        {n.organismo} · {formatFecha(n.fechaPublicacion)}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={`${nivelImpacto(n.colorImpacto)} (${n.puntajeImpacto})`}
                          onClick={() => handleOpenImpacto(n)}
                          sx={{ bgcolor: colorHex(n.colorImpacto), color: "#fff", fontWeight: 600, cursor: "pointer" }} />
                        {data.incluyeEstadoGestion && n.estadoGestion && (
                          <Chip size="small" label={n.estadoGestion}
                            sx={{ bgcolor: n.estadoGestionColor || "#9E9E9E", color: "#fff", fontWeight: 600 }} />
                        )}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]} component="div" count={normas.length}
                  rowsPerPage={rowsPerPageNormas} page={pageNormas}
                  onPageChange={handleChangePageNormas} onRowsPerPageChange={handleChangeRowsPerPageNormas}
                  labelRowsPerPage="Por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
              </>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Regulador</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Norma</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Impacto</TableCell>
                        {data.incluyeEstadoGestion && <TableCell sx={{ fontWeight: 600 }} align="center">Estado</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {normasPagina.map((n) => (
                        <TableRow key={n.id} hover>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{formatFecha(n.fechaPublicacion)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{n.organismo}</TableCell>
                          <TableCell sx={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {n.nombre}
                          </TableCell>
                          <TableCell align="center">
                            <Chip size="small" label={`${nivelImpacto(n.colorImpacto)} (${n.puntajeImpacto})`}
                              onClick={() => handleOpenImpacto(n)}
                              sx={{ bgcolor: colorHex(n.colorImpacto), color: "#fff", fontWeight: 600, fontSize: "0.72rem", cursor: "pointer" }} />
                          </TableCell>
                          {data.incluyeEstadoGestion && (
                            <TableCell align="center">
                              {n.estadoGestion ? (
                                <Chip size="small" label={n.estadoGestion}
                                  sx={{ bgcolor: n.estadoGestionColor || "#9E9E9E", color: "#fff", fontWeight: 600, fontSize: "0.72rem" }} />
                              ) : (
                                <Typography variant="caption" color="text.disabled">—</Typography>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]} component="div" count={normas.length}
                  rowsPerPage={rowsPerPageNormas} page={pageNormas}
                  onPageChange={handleChangePageNormas} onRowsPerPageChange={handleChangeRowsPerPageNormas}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
              </>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ mt: 3, p: 2, bgcolor: "action.hover", display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Nivel de impacto:</strong> Bajo 0–1 · Medio 2–3 · Alto 4–5.
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
        <Typography variant="body2" color="text.secondary">
          <strong>Criterios:</strong> sanción, vigencia inmediata, riesgo reputacional, costo de implementación e impacto operativo.
        </Typography>
      </Paper>

      {openImpactoModal && (
        <DetalleImpactoModal
          open={openImpactoModal}
          onClose={() => setOpenImpactoModal(false)}
          token={token}
          idUsuario={idUsuario}
          normativa={normativaImpacto}
          esAdmin={esAdmin}
          onGuardado={fetchDashboard}
        />
      )}
    </Box>
  );
};

export default DashboardImpacto;